import { useEffect, useState } from 'react';

import { SelectChangeEvent, Stack } from '@mui/material';

import { CfsApi } from '@/api';
import { AppModal, Select, SelectItem } from '@/components';
import { LocalStorageItems } from '@/constants';
import { IUser } from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

interface UpdatePrimaryOfficerModalProps {
  cfsId: string;
  open: boolean;
  officer?: string;
  onClose: () => void;
}

const UpdatePrimaryOfficerModal = ({
  cfsId,
  officer,
  open,
  onClose,
}: UpdatePrimaryOfficerModalProps) => {
  const { updateToast } = useToastStore();
  const [respondingOfficers, setRespondingOfficers] = useState<SelectItem[]>([
    { value: 'n/a', label: 'N/A' },
  ]);
  const [selectedOfficerId, setSelectedOfficerId] = useState<string>('n/a');
  const [loading, setLoading] = useState<boolean>(false);

  const { userAgencyId } = useUserStore();

  const handleClose = () => {
    onClose();
  };

  const handlePrimaryOfficerChange = (e: SelectChangeEvent<unknown>) => {
    const newPrimaryOfficer = e.target.value as string;
    if (newPrimaryOfficer && newPrimaryOfficer !== 'n/a') {
      setSelectedOfficerId(newPrimaryOfficer);
      submitPrimaryOfficerChange(newPrimaryOfficer);
    }
  };

  const fetchAllRespondingOfficers = async () => {
    setLoading(true);
    try {
      const officers: any[] = [];
      const response = await CfsApi.getOne(cfsId);
      response.data.assignedUnitShifts.forEach((shift) => {
        if (
          shift.unitShift.unit?.agency === userAgencyId &&
          shift.unitShift.users
        ) {
          officers.push(...shift.unitShift.users);
        }
      });

      const uniqueOfficers = officers.reduce((acc, user) => {
        if (!acc.some((o: IUser) => o._id === user._id)) {
          acc.push(user);
        }

        return acc;
      }, []);

      const officersList = [
        { value: 'n/a', label: 'N/A' },
        ...uniqueOfficers.map((user: IUser) => ({
          value: user._id,
          label: user.fullName,
        })),
      ];

      setRespondingOfficers(officersList);

      // Determine if the officer prop is a valid ID in our list
      if (officer) {
        const isValidId = officersList.some((item) => item.value === officer);
        const matchingOfficer = officersList.find(
          (item) => item.label === officer,
        );

        if (isValidId) {
          setSelectedOfficerId(officer);
        } else if (matchingOfficer) {
          // If officer is a name instead of ID, use the corresponding ID
          setSelectedOfficerId(matchingOfficer.value);
        } else {
          setSelectedOfficerId('n/a');
        }
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (cfsId) {
      fetchAllRespondingOfficers();
    }
  }, []);

  const submitPrimaryOfficerChange = (primaryOfficer: string) => {
    if (!primaryOfficer) {
      return;
    }

    if (userAgencyId) {
      localStorage.setItem(LocalStorageItems.CurrentAgencyId, userAgencyId);
    }

    CfsApi.updatePrimaryOfficer(cfsId, primaryOfficer)
      .then(() => {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Updated CFS primary officer',
        });

        setTimeout(() => {
          handleClose();
        }, 2000);
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  return (
    <AppModal open={open} onClose={handleClose} title="Assign Primary Officer">
      <Stack spacing={2}>
        {loading ? (
          <div className="loading-spinner">
            <span>Loading...</span>
          </div>
        ) : (
          <Select
            disabled={loading}
            label="Select Primary Officer"
            value={selectedOfficerId}
            items={respondingOfficers}
            onChange={handlePrimaryOfficerChange}
          />
        )}
      </Stack>
    </AppModal>
  );
};

export default UpdatePrimaryOfficerModal;
